'use client';

import { type CSSProperties, type HTMLAttributes } from 'react';
import { cn } from '../../lib/cn';

/** 라디오 값 타입 (sd-radio SRadioValue) */
export type SRadioValue = string | number | boolean;

export interface SRadioProps {
  /** 이 라디오의 값 */
  val: SRadioValue;
  /** 그룹의 현재 선택 값 */
  value?: SRadioValue | null;
  /** 라벨 */
  label?: string;
  /** 비활성 */
  disabled?: boolean;
  /** native radio name (form-agnostic 그룹핑) */
  name?: string;
  /** 선택 변경 (sdUpdate) — 선택된 val 전달 */
  onValueChange?: (val: SRadioValue) => void;
  className?: string;
  style?: CSSProperties;
}

/** SRadio — sd-radio 포팅. value===val 이면 선택. 단독 또는 SRadioGroup 안에서 사용. */
export function SRadio({
  val,
  value,
  label,
  disabled = false,
  name,
  onValueChange,
  className,
  style,
}: SRadioProps) {
  // key/name 속성용 문자열 표현
  const valStr = String(val);
  const checked = value === val;
  const select = () => {
    if (!disabled && !checked) onValueChange?.(val);
  };

  // name 이 있으면 네이티브 <input type="radio"> 가 실제 상호작용 컨트롤이 된다.
  // 이때 label 에는 role/tabIndex/키핸들러를 주지 않는다 — 상호작용 요소 중첩
  // (role="radio" 안에 native radio) 및 radio 역할 중복을 피한다.
  const native = name != null && name !== '';

  // native 모드가 아니면 label 자체가 커스텀 radio 로 동작한다.
  const labelInteractiveProps: HTMLAttributes<HTMLLabelElement> = native
    ? {}
    : {
        'role': 'radio',
        'aria-checked': checked,
        'tabIndex': disabled ? -1 : 0,
        'onClick': select,
        'onKeyDown': e => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            select();
          }
        },
      };

  return (
    <label
      {...labelInteractiveProps}
      className={cn(
        'inline-flex items-center gap-[8px]',
        disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        className,
      )}
      style={style}
    >
      {native && (
        <input
          type="radio"
          name={name}
          value={valStr}
          checked={checked}
          disabled={disabled}
          // 네이티브 라디오가 포커스/키보드/선택을 담당한다. label 이 감싸므로
          // 라벨 클릭도 이 input 을 토글하며, onChange 는 값이 실제로 바뀔 때만 발생.
          onChange={() => onValueChange?.(val)}
          className="sr-only"
        />
      )}
      <span
        className={cn(
          'flex h-[16px] w-[16px] flex-shrink-0 items-center justify-center rounded-full border border-solid border-[color:var(--rd-border)] bg-[var(--rd-bg)]',
          !disabled &&
            !checked &&
            'hover:border-[color:var(--cmp-radio-border-hover)] hover:bg-[var(--cmp-radio-bg-hover)]',
        )}
        style={
          {
            '--rd-bg': disabled ? 'var(--cmp-radio-bg-disabled)' : 'var(--cmp-radio-bg-default)',
            '--rd-border': disabled
              ? 'var(--cmp-radio-border-disabled)'
              : checked
                ? 'var(--cmp-radio-border-active)'
                : 'var(--cmp-radio-border-default)',
          } as CSSProperties
        }
      >
        {checked && (
          <span
            className="h-[10px] w-[10px] rounded-full"
            style={{
              background: disabled
                ? 'var(--cmp-radio-dot-disabled)'
                : 'var(--cmp-radio-dot-default)',
            }}
          />
        )}
      </span>
      {label && (
        <span
          className="grid text-[12px] leading-[20px]"
          style={{
            color: disabled ? 'var(--cmp-radio-label-disabled)' : 'var(--cmp-radio-label-default)',
          }}
        >
          <span aria-hidden className="invisible font-medium" style={{ gridArea: '1 / 1' }}>
            {label}
          </span>
          <span style={{ gridArea: '1 / 1', fontWeight: checked ? 500 : 400 }}>{label}</span>
        </span>
      )}
    </label>
  );
}
